/**
 * VaccinationTable (#553) — the front-desk's #1 compliance surface on the Pet
 * Profile. Per-shot status reuses the SAME server derivation as the status
 * strip + ID card (`status` on each row), so a lapsed shot reads red here
 * exactly as elsewhere (Principle 1 — one truth). Status carries a text mark
 * AND label beside its color (never color-only). Provenance ("Last updated")
 * and the verifier name are the audit trail.
 *
 * Wide table scrolls inside its own container — the page body never scrolls
 * horizontally (Principle 7 / mobile-first).
 *
 * Ted #32a (#1156) — a row that a later record of the same vaccine replaced
 * reads "Replaced by a newer record" in neutral and sorts after the current
 * ones, so the desk sees which record decides the dog's status.
 *
 * #752 — when a row was approved from an uploaded record, its "Record" cell
 * opens that original. The column appears only when at least one row has
 * one, so a dog with no uploads keeps the table it always had.
 *
 * Ted #32a (E1.6b, E1.7) — the desk can now fix what it reads:
 *   · the vaccine name is the row's one control (`onManage`): it opens the
 *     record to correct, verify, delete or attach the certificate. It sits in
 *     the first column so it is on screen at 375px without scrolling the
 *     table sideways;
 *   · "Verification" says who stands behind the values. An unsigned Gingr
 *     record reads "From Gingr", not "Unverified" (vaccination-source.ts),
 *     and "Source" names where the values came from;
 *   · a certificate staff attached opens from the same "Record" cell as an
 *     uploaded original, and wins when a row has both (it is the newer act).
 */
import {
  VACCINATION_SOURCE_LABEL,
  VACCINATION_STANDING_LABEL,
  parseVaccinationSource,
  vaccinationStanding,
} from '@ferro/core/modules/dog-profile/vaccination-source.js';
import type { DogProfileVaccination } from '../../lib/api.js';
import type { OpenDocument } from './DogVaxRecords.js';
import {
  currentRowsFirst,
  formatDate,
  vaccineLabel,
  vaxRowDisplay,
  VAX_ROW_LABEL,
  VAX_ROW_MARK,
} from './format.js';

interface VaccinationTableProps {
  vaccinations: ReadonlyArray<DogProfileVaccination>;
  /** #752 — vaccination id → the original it was approved from. */
  sources?: ReadonlyMap<string, OpenDocument>;
  onViewSource?: (open: OpenDocument) => void;
  /** Ted #32a (E1.6c) — open the certificate staff attached to this row. */
  onViewCertificate?: (v: DogProfileVaccination) => void;
  /**
   * Ted #32a (E1.6b) — open this record to manage it. Absent for a role
   * without `vaccinations:write:all`: the name then reads as plain text.
   */
  onManage?: (v: DogProfileVaccination) => void;
}

/** "Verified · Riley", "From Gingr", "Entered by staff" or "Unverified". */
function standingText(v: DogProfileVaccination): string {
  const standing = vaccinationStanding({ verified: v.verified, source: v.source ?? null });
  const label = VACCINATION_STANDING_LABEL[standing.kind];
  return standing.kind === 'verified' && v.verifiedByName ? `${label} · ${v.verifiedByName}` : label;
}

function sourceText(v: DogProfileVaccination): string {
  const source = parseVaccinationSource(v.source ?? null);
  return source ? VACCINATION_SOURCE_LABEL[source] : '—';
}

export function VaccinationTable({
  vaccinations,
  sources,
  onViewSource,
  onViewCertificate,
  onManage,
}: VaccinationTableProps): JSX.Element {
  const hasCertificate = (v: DogProfileVaccination): boolean => !!onViewCertificate && !!v.document;
  const hasOriginal = (v: DogProfileVaccination): boolean =>
    !!onViewSource && !!sources && sources.has(v.id);
  const showRecord = vaccinations.some((v) => hasCertificate(v) || hasOriginal(v));
  // An older server sends no `source`; the column would be all dashes.
  const showSource = vaccinations.some((v) => v.source !== undefined);
  return (
    <div className="ferro-dogprofile__table-scroll">
      <table
        className="ferro-dogprofile__table"
        data-testid="dogprofile-vax-table"
      >
        <thead>
          <tr>
            <th scope="col">Vaccine</th>
            <th scope="col">Given</th>
            <th scope="col">Expires</th>
            <th scope="col">Status</th>
            <th scope="col">Verification</th>
            {showSource ? <th scope="col">Source</th> : null}
            <th scope="col">Last updated</th>
            {showRecord ? <th scope="col">Record</th> : null}
          </tr>
        </thead>
        <tbody>
          {currentRowsFirst(vaccinations).map((v) => {
            const given = formatDate(v.dateGiven);
            const expires = formatDate(v.expiresAt);
            const updated = formatDate(v.updatedAt);
            const shown = vaxRowDisplay(v);
            const verified = v.verified === true;
            return (
              <tr
                key={v.id}
                className={`ferro-dogprofile__vax-row ferro-dogprofile__vax-row--${shown}`}
                data-testid={`dogprofile-vax-${shown}`}
                data-vaccination-id={v.id}
              >
                <td>
                  {onManage ? (
                    <button
                      type="button"
                      className="ferro-dogprofile__vax-manage"
                      onClick={() => onManage(v)}
                      aria-label={`Manage the ${vaccineLabel(v.name)} record given ${given ?? 'on an unknown date'}`}
                      data-testid="dogprofile-vax-manage"
                    >
                      <span className="ferro-dogprofile__vax-name">{vaccineLabel(v.name)}</span>
                      <span className="ferro-dogprofile__vax-manage-hint" aria-hidden="true">
                        Edit ›
                      </span>
                    </button>
                  ) : (
                    <span className="ferro-dogprofile__vax-name">{vaccineLabel(v.name)}</span>
                  )}
                </td>
                <td className="ferro-dogprofile__fact-value--num">
                  {given ?? '—'}
                </td>
                <td className="ferro-dogprofile__fact-value--num">
                  {expires ?? '—'}
                </td>
                <td>
                  <span className="ferro-dogprofile__vax-status">
                    <span
                      className="ferro-dogprofile__vax-mark"
                      aria-hidden="true"
                    >
                      {VAX_ROW_MARK[shown]}
                    </span>
                    {VAX_ROW_LABEL[shown]}
                  </span>
                </td>
                <td data-testid="dogprofile-vax-standing">
                  <span className={verified ? 'ferro-dogprofile__verified' : 'ferro-dogprofile__unverified'}>
                    {standingText(v)}
                  </span>
                </td>
                {showSource ? <td data-testid="dogprofile-vax-source">{sourceText(v)}</td> : null}
                <td className="ferro-dogprofile__fact-value--num">
                  {updated ?? '—'}
                </td>
                {showRecord ? (
                  <td>
                    {hasCertificate(v) ? (
                      <button
                        type="button"
                        className="ferro-btn ferro-btn--ghost ferro-dogprofile__vax-view"
                        onClick={() => onViewCertificate!(v)}
                        aria-label={`View the ${v.name} certificate`}
                        data-testid="dogprofile-vax-certificate-view"
                      >
                        View
                      </button>
                    ) : hasOriginal(v) ? (
                      <button
                        type="button"
                        className="ferro-btn ferro-btn--ghost ferro-dogprofile__vax-view"
                        onClick={() => onViewSource!(sources!.get(v.id)!)}
                        aria-label={`View the ${v.name} record`}
                        data-testid="dogprofile-vax-row-view"
                      >
                        View
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
